import { apiRequestEnvelope } from '@scc/lib'
import type {
  LaunchRepository,
  LaunchProgressMonth,
  LaunchProgressMeta,
  Coverage,
  CoverageKey,
} from '@/data/repositories/types'

/**
 * The feed as the wire carries it. Everything past the eight original fields
 * is optional here because the backend that sends it (cc-api #222) is not yet
 * deployed, and an older response must still parse.
 */
type RawMonth = Partial<LaunchProgressMonth> & Record<string, unknown>

const COVERAGE_KEYS: CoverageKey[] = ['shop', 'ads', 'adAttribution', 'videos', 'samples', 'buyers', 'newCustomers', 'dtc']

/** A number as the feed sent it — 0 for the original fields when it sent nothing, as it always has. */
const legacy = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

/**
 * A new field: `null` means the source had nothing to say, `undefined` means
 * this response predates the field. Neither is ever coerced to 0 — a 0 here
 * would read as a measured zero, which is the bug the coverage contract exists
 * to prevent.
 */
const optional = (v: unknown): number | null | undefined =>
  v === null ? null : typeof v === 'number' && Number.isFinite(v) ? v : undefined

function coverageOf(raw: unknown): Record<CoverageKey, Coverage> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Record<string, unknown>
  const out = {} as Record<CoverageKey, Coverage>
  for (const key of COVERAGE_KEYS) {
    const c = obj[key]
    // A row whose coverage is incomplete is treated as carrying none: half a
    // verdict is worse than an honest "coverage not reported".
    if (!c || typeof c !== 'object') return undefined
    out[key] = c as Coverage
  }
  return out
}

/** One feed row as the plan page consumes it. Exported for the adapter test. */
export function toLaunchProgressMonth(raw: RawMonth): LaunchProgressMonth {
  const row: LaunchProgressMonth = {
    year: legacy(raw.year),
    month: legacy(raw.month),
    gmv: legacy(raw.gmv),
    orders: legacy(raw.orders),
    adSpend: legacy(raw.adSpend),
    videos: legacy(raw.videos),
    creators: legacy(raw.creators),
    samples: legacy(raw.samples),
    dtcAdSpend: legacy(raw.dtcAdSpend),
    dtcRevenue: legacy(raw.dtcRevenue),
  }
  const put = (key: 'skuOrders' | 'itemsSold' | 'adRevenue' | 'adOrders' | 'uniqueBuyers' | 'samplesCancelled' | 'videosPosted') => {
    const v = optional(raw[key])
    if (v !== undefined) row[key] = v
  }
  put('skuOrders'); put('itemsSold'); put('adRevenue'); put('adOrders')
  put('uniqueBuyers'); put('samplesCancelled'); put('videosPosted')
  if ('newCustomers' in raw) row.newCustomers = null
  if (raw.paid !== undefined) row.paid = raw.paid as LaunchProgressMonth['paid']
  if (raw.attributedGmv && typeof raw.attributedGmv === 'object') row.attributedGmv = raw.attributedGmv as LaunchProgressMonth['attributedGmv']
  if (raw.funnel && typeof raw.funnel === 'object') row.funnel = raw.funnel as LaunchProgressMonth['funnel']
  const coverage = coverageOf(raw.coverage)
  if (coverage) row.coverage = coverage
  if (raw.financials && typeof raw.financials === 'object') row.financials = raw.financials as LaunchProgressMonth['financials']
  return row
}

export function createApiLaunchRepository(): LaunchRepository {
  async function fetchProgress(
    clientId: string,
    start: string,
    months: number,
  ): Promise<{ months: LaunchProgressMonth[]; dataThrough: string | null; meta?: LaunchProgressMeta }> {
    const { data, meta } = await apiRequestEnvelope<RawMonth[], LaunchProgressMeta>(
      `/analytics/launch/progress?start=${encodeURIComponent(start)}&months=${months}`,
      { headers: { 'X-Store-Id': clientId } },
    )
    const rows = Array.isArray(data) ? data.map(toLaunchProgressMonth) : []
    return { months: rows, dataThrough: meta?.dataThrough ?? null, meta: meta ?? undefined }
  }

  return {
    async getProgressActuals(
      clientId: string,
      start: string,
      months: number,
    ): Promise<LaunchProgressMonth[]> {
      const result = await fetchProgress(clientId, start, months)
      return result.months
    },
    getProgressActualsWithMeta: fetchProgress,
  }
}
